import type { StaffMember } from '@/mocks/adminStaff';
import { roleLabels, roleColors, roleDescriptions, permissionLabels, permissionColors, statusLabels, statusColors } from '@/mocks/adminStaff';

interface StaffCardProps {
  member: StaffMember;
  onEdit: (member: StaffMember) => void;
  onDelete: (member: StaffMember) => void;
  onSuspend: (member: StaffMember) => void;
  onReactivate: (member: StaffMember) => void;
  onResendInvite: (member: StaffMember) => void;
}

export default function StaffCard({ member, onEdit, onDelete, onSuspend, onReactivate, onResendInvite }: StaffCardProps) {
  const socialIcons: Record<string, string> = {
    instagram: 'ri-instagram-line',
    twitter: 'ri-twitter-x-line',
    youtube: 'ri-youtube-line',
    tiktok: 'ri-tiktok-line',
    website: 'ri-global-line',
  };

  const hasSocialLinks = Object.values(member.socialLinks).some((v) => v);

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <div className="w-14 h-14 rounded-full bg-background-200 overflow-hidden shrink-0">
          <img
            src={member.photoUrl}
            alt={member.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-foreground-900">{member.name}</h3>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${statusColors[member.status]}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                member.status === 'active' ? 'bg-emerald-500' :
                member.status === 'invited' ? 'bg-amber-500' :
                member.status === 'suspended' ? 'bg-red-500' :
                'bg-foreground-400'
              }`}></span>
              {statusLabels[member.status]}
            </span>
          </div>
          <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold ${roleColors[member.role]}`}>
            {roleLabels[member.role]}
          </span>
          <p className="text-[11px] text-foreground-500 mt-0.5 truncate">{member.email}</p>
          {member.bio && (
            <p className="text-[11px] text-foreground-500 mt-1.5 leading-relaxed line-clamp-2">{member.bio}</p>
          )}
          {member.status === 'suspended' && member.suspensionReason && (
            <p className="text-[10px] text-red-500 mt-1.5 leading-relaxed">
              Motivo: {member.suspensionReason}
            </p>
          )}
          {member.status === 'invited' && member.invitedAt && (
            <p className="text-[10px] text-foreground-400 mt-1">
              Invitado el {new Date(member.invitedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
            </p>
          )}
        </div>
      </div>

      {/* Role description */}
      <div className="px-4 pb-3">
        <div className="bg-background-100 rounded-xl p-2.5">
          <p className="text-[10px] text-foreground-500 leading-relaxed">{roleDescriptions[member.role]}</p>
        </div>
      </div>

      {/* Permissions */}
      {member.permissions.length > 0 && (
        <div className="px-4 pb-3">
          <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Permisos</p>
          <div className="flex items-center gap-1 flex-wrap">
            {member.permissions.map((perm) => (
              <span
                key={perm}
                className={`inline-block px-1.5 py-0.5 rounded-md text-[10px] font-semibold whitespace-nowrap ${permissionColors[perm]}`}
              >
                {permissionLabels[perm]}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Programs */}
      {member.assignedPrograms.length > 0 && (
        <div className="px-4 pb-3">
          <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Programas</p>
          <div className="space-y-0.5">
            {member.assignedPrograms.map((prog) => (
              <span key={prog} className="block text-[11px] text-foreground-600">
                {prog}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Social links */}
      {hasSocialLinks && (
        <div className="px-4 pb-3">
          <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Redes</p>
          <div className="flex items-center gap-2 flex-wrap">
            {Object.entries(member.socialLinks).map(([platform, handle]) =>
              handle ? (
                <span key={platform} className="text-[10px] text-foreground-500 flex items-center gap-1">
                  <i className={`${socialIcons[platform] || 'ri-link'} text-[11px]`}></i>
                  {handle}
                </span>
              ) : null
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-4 py-3 border-t border-background-100 space-y-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(member)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-700 text-xs font-semibold hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-pencil-line text-xs"></i>
            Editar
          </button>
          <button
            onClick={() => onDelete(member)}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-delete-bin-line text-xs"></i>
            Eliminar
          </button>
        </div>

        <div className="flex items-center gap-2">
          {member.status === 'invited' && (
            <button
              onClick={() => onResendInvite(member)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-amber-50 text-amber-700 text-[11px] font-semibold hover:bg-amber-100 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-mail-send-line text-xs"></i>
              Reenviar
            </button>
          )}
          {(member.status === 'active' || member.status === 'invited') && (
            <button
              onClick={() => onSuspend(member)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-red-50 text-red-700 text-[11px] font-semibold hover:bg-red-100 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-pause-circle-line text-xs"></i>
              Suspender
            </button>
          )}
          {(member.status === 'suspended' || member.status === 'inactive') && (
            <button
              onClick={() => onReactivate(member)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-[11px] font-semibold hover:bg-emerald-100 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-xs"></i>
              Reactivar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}